import { useRef, useState } from 'react';
import classNames from 'classnames';
import { Divider, makeStyles, Paper, Typography } from '@material-ui/core';
import { get, includes, noop, startCase } from 'lodash';
import grey from '@material-ui/core/colors/grey';

import { useDevCenterRouter } from '../DevCenter';

import Actions from './components/Actions';
import FeedComments from './components/FeedComments';
import Content from './components/Content';
import CreatedAt from './components/CreatedAt';
import Icon from './components/Icon';
import Reactions from './components/Reactions';
import RigAndWell from './components/RigAndWell';
import TypeAndAssetTitle from './components/TypeAndAssetTitle';
import CompletionDateTimestamp from './components/CompletionDateTimestamp';

import styles from './FeedItem.css';

import { isNativeDetected } from '~/utils/mobileDetect';
import ConfirmationDialog from '~/components/ConfirmationDialog';
import LoadingIndicator from '~/components/LoadingIndicator';
import ErrorBoundary from '~/components/ErrorBoundary';
import { useIsInsideIsolatedDcApp } from '~/components/DevCenter/DevCenterAppContainer/components';
import { FEED_ITEM_TYPES, FEED_ITEM_TYPES_BY_KEY } from '~/constants/feed';
import { deleteFeedItem, toggleFeedItemLike } from '~/clients/jsonApi';
import { showSuccessNotification } from '~/utils';
import utils from '~/utils/main';
import { OPEN_ALERT_DETAILS_ID_STORAGE_KEY } from '~/constants/alerts';

const PAGE_NAME = 'FeedPo';

const useStyles = makeStyles(theme => ({
  paperRoot: {
    position: 'relative',
    marginBottom: 10,
    paddingBottom: 16,
    backgroundColor: ({ isInsideDcApp }) =>
      isInsideDcApp ? 'rgba(65, 65, 65, 0.9)' : theme.palette.background.b8,
    width: ({ isInsideDcApp }) => (isInsideDcApp ? 360 : '100%'),
    boxShadow: ({ isInsideDcApp }) =>
      isInsideDcApp &&
      '0px 8px 10px rgba(0, 0, 0, 0.14), 0px 3px 14px rgba(0, 0, 0, 0.12), 0px 5px 5px rgba(0, 0, 0, 0.2)',
  },
  clickablePaperRoot: {
    '&:hover': {
      filter: 'brightness(90%)',
      transition: 'filter 0.3s',
    },
  },
  headerDividerRoot: {
    margin: '8px 0 8px 0',
    backgroundColor: '#595959',
  },
  dateTimestamp: {
    color: theme.palette.primary.text6,
    '& span': {
      color: theme.palette.primary.contrastText,
    },
  },
}));

/**
 * @typedef {object} ActivityItem
 * @property {number} id
 * @property {string} type
 * @property {Record<string, any>} [context]
 * @property {Array<'update' | 'destroy' | 'like'>} [allowed_actions]
 * @property {number} [company_id]
 * @property {number} [asset_id] is not received from the back but have to be provided while saving activities
 * @property {any} [rig]
 * @property {any} [well]
 * @property {string} [updated_at]
 * @property {any} [segment]
 * @property {boolean} [liked]
 * @property {any[]} [likes]
 * @property {number} [likes_count]
 * @property {any[]} [mentioned_users]
 */

/**
 * @param {object} props
 * @param {ActivityItem} props.feedItem
 * @param {string[]} [props.editableFeedItemTypes]
 * @param {(feedItemId: number) => void} [props.removeFeedItem] called after a feed item was removed by a user
 * @param {(feedItem: ActivityItem) => void} [props.enterEditMode] called when a used clicks "Edit" menu item
 * @param {any} [props.router]
 * @param {any} props.currentUser used to display Avatar and to determine the ability to edit comments
 * @param {any} [props.appData] passed to body Template
 * @param {Record<string, any>} [props.customFeedItemTypeTemplates]
 * @param {string} [props.paperClassName]
 * @param {any} [props.getFeedItemMessage] used only to create a data-testid attribute
 * @param {(feedItem: ActivityItem) => void} [props.onFeedItemClick]
 * used to override built-in navigation for clickable alert feed items
 * @param {ReactNode} [props.extraActions] additional action nodes rendered inside the reactions/actions row, after the built-in like and comment buttons
 */
function FeedItem({
  feedItem,
  editableFeedItemTypes = [FEED_ITEM_TYPES.POST],
  removeFeedItem = noop,
  enterEditMode = noop,
  router,
  currentUser,
  appData,
  customFeedItemTypeTemplates = {},
  paperClassName,
  getFeedItemMessage = ({ context }) => context?.post?.body || context?.dvd_comment?.body,
  onFeedItemClick,
  extraActions,
  ...props
}) {
  const isCorvaV3FeaturesEnabled = get(currentUser, [
    'settings',
    'uiSettings',
    'corva_v3_features_enabled',
  ]);

  const canDeleteFeedItem = includes(feedItem.allowed_actions, 'destroy');

  const isInsideDcApp = useIsInsideIsolatedDcApp();
  const classes = useStyles({ isInsideDcApp });
  const [isLoading, setIsLoading] = useState(false);
  const [openCommentInput, setOpenCommentInput] = useState(true);
  const [openCommentList, setOpenCommentList] = useState(true);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  const isPost = feedItem.type === FEED_ITEM_TYPES_BY_KEY.post.type;
  const isEditableItem = editableFeedItemTypes.includes(feedItem.type);

  const isGeosteering = feedItem.type === FEED_ITEM_TYPES_BY_KEY.geosteering_comments.type;
  const isRecommended =
    isGeosteering && feedItem.context?.geosteering_comments?.data?.isRecommendation;

  const typeCategory = FEED_ITEM_TYPES_BY_KEY[feedItem.type]?.category || 'post';

  const userFullName = utils.getUserFullName(feedItem.user);
  const typeLabel = FEED_ITEM_TYPES_BY_KEY[feedItem.type]?.showCreatorToLabel
    ? userFullName
    : FEED_ITEM_TYPES_BY_KEY[feedItem.type]?.label || startCase(feedItem.type);

  const feedItemSourceLink =
    feedItem.type === FEED_ITEM_TYPES_BY_KEY.alert.type && `/alerts/${feedItem.context?.alert?.id}`;

  const isFeedItemClickable =
    feedItem.type === FEED_ITEM_TYPES_BY_KEY.alert.type && feedItemSourceLink;

  const handleDeleteFeedItem = async () => {
    setIsLoading(true);

    try {
      await deleteFeedItem(feedItem.id);
    } catch (e) {
      return;
    } finally {
      setIsLoading(false);
    }

    showSuccessNotification('Item was deleted');

    removeFeedItem(feedItem.id);
  };

  const isBeingLikedRef = useRef(false);

  const handleToggleLike = async () => {
    if (!isBeingLikedRef.current) {
      const feedItemId = feedItem.id;
      isBeingLikedRef.current = true;
      try {
        await toggleFeedItemLike(feedItemId); // NOTE: Received through subscription
      } catch (e) {
        // NOTE: Do nothing
      }
      isBeingLikedRef.current = false;
    }
  };

  const handleEditMenuItem = () => {
    enterEditMode(feedItem);
  };

  const handleDeleteMenuItem = () => {
    setOpenDeleteDialog(true);
  };

  const copyFeedItemLink = () => {
    const feedId = feedItem.id;
    const text = `${window.location.host}/feed/${feedId}`;

    utils.copyToClipboard(text);
  };

  const handleDelete = () => {
    handleDeleteFeedItem();
  };

  const devCenterRouter = useDevCenterRouter();

  const handleMainContainerClick = () => {
    if (!isFeedItemClickable) return;

    if (onFeedItemClick) {
      onFeedItemClick(feedItem);
      return;
    }

    if (isCorvaV3FeaturesEnabled)
      localStorage.setItem(OPEN_ALERT_DETAILS_ID_STORAGE_KEY, feedItem.context.alert.id);
    else (router || devCenterRouter).push(feedItemSourceLink);
  };

  const getRigName = feedItem => {
    const isRigBasedItem = !!feedItem.rig;
    return isRigBasedItem ? feedItem.rig?.name : feedItem.well?.parent_asset?.name;
  };

  return (
    <Paper
      data-testid={`${PAGE_NAME}_post_${getFeedItemMessage(feedItem)}_rigName_${getRigName(
        feedItem
      )}`}
      className={classNames(classes.paperRoot, paperClassName, {
        [classes.clickablePaperRoot]: isFeedItemClickable,
        [styles.hideOverflow]: isLoading,
      })}
      elevation={0}
    >
      <div
        onClick={handleMainContainerClick}
        className={classNames(styles.mainContainer, {
          [styles.mainContainerClickable]: isFeedItemClickable,
          [styles.mainContainerNative]: isNativeDetected,
        })}
      >
        <Icon
          feedItem={feedItem}
          classes={classes}
          displayName={userFullName}
          isRecommended={isRecommended}
          currentUser={currentUser}
        />
        <div
          className={classNames(styles.mainContainerTop, {
            [styles.mainContainerTopNative]: isNativeDetected,
          })}
        >
          <div className={styles.mainContainerTopLine}>
            <TypeAndAssetTitle
              feedItem={feedItem}
              userFullName={userFullName}
              typeLabel={typeLabel}
              classes={classes}
              isPost={isPost}
            />
            <div className={styles.mainContainerTopMenu}>
              <CreatedAt feedItem={feedItem} classes={classes} />

              <Actions
                data-testid={`${PAGE_NAME}_actionsDropdown`}
                isPost={isPost}
                isEditableItem={isEditableItem}
                canDeleteFeedItem={canDeleteFeedItem}
                handleEditMenuItem={handleEditMenuItem}
                handleDeleteMenuItem={handleDeleteMenuItem}
                copyFeedItemLink={copyFeedItemLink}
                feedItem={feedItem}
                currentUser={currentUser}
              />
            </div>
          </div>

          <RigAndWell feedItem={feedItem} isPost={isPost} />
        </div>
        <Divider className={classes.headerDividerRoot} />
        <CompletionDateTimestamp feedItem={feedItem} />
        <ErrorBoundary ErrorView={() => <Typography>Feed Item loading failed</Typography>}>
          <Content
            feedItem={feedItem}
            appData={appData}
            customFeedItemTypeTemplates={customFeedItemTypeTemplates}
            currentUser={currentUser}
            isCorvaV3FeaturesEnabled={isCorvaV3FeaturesEnabled}
            onFeedItemClick={onFeedItemClick}
            {...props}
          />
        </ErrorBoundary>
      </div>
      <Reactions
        feedItem={feedItem}
        handleToggleLike={handleToggleLike}
        setOpenCommentInput={setOpenCommentInput}
        setOpenCommentList={setOpenCommentList}
        openCommentInput={openCommentInput}
        openCommentList={openCommentList}
        currentUser={currentUser}
        extraActions={extraActions}
      />
      <FeedComments
        openCommentInput={openCommentInput}
        openCommentList={openCommentList}
        feedItem={feedItem}
        currentUser={currentUser}
      />
      {isLoading && ( // NOTE: Avoids feed item height change
        <div className={styles.loadingContainer} style={{ backgroundColor: grey[800] }}>
          <LoadingIndicator />
        </div>
      )}
      {openDeleteDialog && (
        <ConfirmationDialog
          open={openDeleteDialog}
          title={`Delete ${typeCategory}`}
          text={`Are you sure you want to delete ${typeCategory} and all attachments?`}
          handleClose={() => setOpenDeleteDialog(false)}
          handleOk={handleDelete}
          okText="Delete"
        />
      )}
    </Paper>
  );
}

export default FeedItem;
